import { Moon, Sun, Monitor, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useThemeStore } from "@/store/theme.store";

export const ThemeToggle = ({ variant = "dropdown" }) => {
  const { t } = useTranslation();
  const { theme, setTheme, toggleTheme } = useThemeStore();

  // Determine effective theme (resolving 'system' to actual value)
  const getEffectiveTheme = () => {
    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    }
    return theme;
  };

  const effectiveTheme = getEffectiveTheme();

  // Simple toggle button (light/dark only)
  if (variant === "simple") {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="h-9 w-9 relative"
      >
        <Sun
          className={cn(
            "h-4 w-4 transition-all",
            effectiveTheme === "dark"
              ? "rotate-90 scale-0"
              : "rotate-0 scale-100",
          )}
        />
        <Moon
          className={cn(
            "absolute h-4 w-4 transition-all",
            effectiveTheme === "dark"
              ? "rotate-0 scale-100"
              : "-rotate-90 scale-0",
          )}
        />
        <span className="sr-only">{t("common.toggleTheme")}</span>
      </Button>
    );
  }

  // Dropdown with light/dark/system options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9 relative">
          <Sun
            className={cn(
              "h-4 w-4 transition-all",
              effectiveTheme === "dark"
                ? "rotate-90 scale-0"
                : "rotate-0 scale-100",
            )}
          />
          <Moon
            className={cn(
              "absolute h-4 w-4 transition-all",
              effectiveTheme === "dark"
                ? "rotate-0 scale-100"
                : "-rotate-90 scale-0",
            )}
          />
          <span className="sr-only">{t("common.toggleTheme")}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className="flex items-center justify-between"
        >
          <span className="flex items-center">
            <Sun className="mr-2 h-4 w-4" />
            Light
          </span>
          {theme === "light" && <Check className="h-4 w-4 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className="flex items-center justify-between"
        >
          <span className="flex items-center">
            <Moon className="mr-2 h-4 w-4" />
            Dark
          </span>
          {theme === "dark" && <Check className="h-4 w-4 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("system")}
          className="flex items-center justify-between"
        >
          <span className="flex items-center">
            <Monitor className="mr-2 h-4 w-4" />
            System
          </span>
          {theme === "system" && <Check className="h-4 w-4 text-primary" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
